import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import HeroSlider from "./HeroSlider";
import {
  ArrowRight, BadgeCheck, BedDouble, CalendarCheck, CheckCircle2, IndianRupee, PlayCircle, ShieldCheck, Stethoscope, Video,
} from "lucide-react";

const APPOINTMENTS = [
  { time: "09:30", name: "Ananya Sharma", type: "In-person", status: "Checked in", tone: "bg-care-50 text-care-700" },
  { time: "09:45", name: "Rahul Verma", type: "Video", status: "Waiting", tone: "bg-amber-50 text-amber-700" },
  { time: "10:00", name: "Priya Nair", type: "In-person", status: "Confirmed", tone: "bg-brand-50 text-brand-700" },
  { time: "10:15", name: "Mohammed Ali", type: "Video", status: "Confirmed", tone: "bg-brand-50 text-brand-700" },
];

const WARDS = [
  { name: "General ward", used: 18, total: 24 },
  { name: "ICU", used: 7, total: 8 },
  { name: "Private rooms", used: 9, total: 14 },
];

/** Illustrative product preview built in HTML so it stays crisp at any size. */
const ProductPreview = () => (
  <div className="relative rounded-2xl border border-slate-200/80 bg-white/90 p-2 shadow-[0_30px_80px_-30px_rgba(11,35,73,.45)] backdrop-blur">
    <div className="flex items-center gap-1.5 px-3 py-2">
      <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
      <span className="ml-3 truncate rounded-md bg-slate-100 px-3 py-1 text-[11px] text-slate-500">practitioner.healthfrigate.com/appointments</span>
    </div>
    <div className="grid grid-cols-[3.25rem_minmax(0,1fr)] overflow-hidden rounded-xl border border-slate-100 bg-slate-50/70">
      <div className="flex flex-col items-center gap-3 bg-ink-900 py-4">
        <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-brand-500 to-care-500" />
        {[CalendarCheck, Video, BedDouble, IndianRupee].map((Icon, i) => (
          <span key={i} className={`flex h-8 w-8 items-center justify-center rounded-lg ${i === 0 ? "bg-white/15 text-white" : "text-slate-400"}`}>
            <Icon className="h-4 w-4" />
          </span>
        ))}
      </div>
      <div className="min-w-0 space-y-3 p-3 sm:p-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ["Today's visits", "42", "+8 vs last Mon"],
            ["Bed occupancy", "74%", "34 of 46 beds"],
            ["Collected", "₹1.84L", "UPI · card · cash"],
            ["Teleconsults", "12", "3 in queue"],
          ].map(([k, v, h]) => (
            <div key={k} className="rounded-lg border border-slate-100 bg-white p-2.5">
              <p className="truncate text-[10px] font-medium text-slate-500">{k}</p>
              <p className="font-display text-lg font-bold text-ink-900">{v}</p>
              <p className="truncate text-[10px] text-slate-400">{h}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div className="rounded-lg border border-slate-100 bg-white p-3 sm:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-ink-900">Up next</p>
              <span className="text-[10px] text-slate-400">Dr. Meera Iyer · OPD 2</span>
            </div>
            <ul className="space-y-1.5">
              {APPOINTMENTS.map((a) => (
                <li key={a.time} className="flex items-center gap-2 rounded-md px-1.5 py-1 text-[11px] hover:bg-slate-50">
                  <span className="w-9 font-mono text-slate-500">{a.time}</span>
                  <span className="min-w-0 flex-1 truncate font-medium text-ink-900">{a.name}</span>
                  <span className="hidden text-slate-400 sm:inline">{a.type}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${a.tone}`}>{a.status}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-slate-100 bg-white p-3 sm:col-span-2">
            <p className="mb-2 text-xs font-semibold text-ink-900">Bed board</p>
            <div className="space-y-2.5">
              {WARDS.map((w) => (
                <div key={w.name}>
                  <div className="flex justify-between text-[10px] text-slate-500">
                    <span>{w.name}</span>
                    <span>{w.used}/{w.total}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-gradient-to-r from-brand-500 to-care-500" style={{ width: `${(w.used / w.total) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-6 gap-1">
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className={`h-3 rounded-sm ${[1, 4, 9].includes(i) ? "bg-care-300" : i === 6 ? "bg-amber-300" : "bg-brand-400"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const reduce = useReducedMotion();
  const fade = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.21, 0.6, 0.35, 1] },
  });

  return (
    <section className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36">
      {/* backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)]" />
        <div className="absolute -top-40 left-1/2 h-[36rem] w-[60rem] -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-200/70 via-brand-100/40 to-care-100/60 blur-3xl" />
        <div className="absolute right-0 top-40 h-72 w-72 rounded-full bg-care-200/40 blur-3xl" />
      </div>

      <div className="container-x grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-10">
        <div className="max-w-xl">
          <motion.a href="#features" {...fade(0)} className="eyebrow hover:border-brand-300">
            <Video className="h-3.5 w-3.5" />
            Video consultations with digital prescriptions
            <ArrowRight className="h-3.5 w-3.5" />
          </motion.a>
          <motion.h1 {...fade(0.08)} className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.6rem]">
            One connected platform to run your <span className="text-gradient">clinic or hospital</span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-6 text-lg leading-relaxed text-slate-600">
            Appointments, video consultations, e-prescriptions, inpatient beds, pharmacy and GST billing — in one secure, ABDM-ready
            system with dedicated portals for your admins, doctors and patients.
          </motion.p>
          <motion.div {...fade(0.24)} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#contact" className="btn-primary px-6 py-3.5 text-base">
              Book a free demo <ArrowRight className="h-5 w-5" />
            </a>
            <a href="#portals" className="btn-secondary px-6 py-3.5 text-base">
              <PlayCircle className="h-5 w-5 text-brand-600" /> Explore the platform
            </a>
          </motion.div>
          <motion.ul {...fade(0.32)} className="mt-8 grid grid-cols-1 gap-2.5 text-sm text-slate-600 sm:grid-cols-2">
            {["Live in days, not months", "Works for single clinics and multi-site groups", "ABDM, FHIR R4 & SNOMED CT", "GST-ready bills, UPI & card"].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-care-500" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 30, rotateX: 8 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.21, 0.6, 0.35, 1] }}
          className="relative [perspective:1200px]"
        >
          <HeroSlider dashboard={<ProductPreview />} />
        </motion.div>
      </div>

      {/* standards strip */}
      <div className="container-x mt-20 lg:mt-24">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Built on the standards Indian healthcare runs on</p>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            [ShieldCheck, "ABDM ready"],
            [Stethoscope, "SNOMED CT"],
            [BadgeCheck, "HL7 FHIR R4"],
            [BedDouble, "HFR & HPR"],
            [IndianRupee, "GST invoicing"],
            [CheckCircle2, "UPI payments"],
          ].map(([Icon, label]) => (
            <div key={label} className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white/70 px-3 py-3 text-sm font-semibold text-slate-600">
              <Icon className="h-4 w-4 text-brand-600" /> {label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
